import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  Tab,
  Tabs,
  Container,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person,
  Lock,
  Email,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { GlassCard, Kicker, Logo, AppButton } from '../components/ui';
import { mvkgTokens } from '../theme/mvkgTokens';

function TabPanel({ children, value, index }) {
  return value === index && <Box sx={{ pt: 2 }}>{children}</Box>;
}

// Prototype-style rounded input with brand focus ring (translated from Login.html).
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: `${mvkgTokens.radius.sm}px`,
    bgcolor: mvkgTokens.surface.paper,
    transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
    '& fieldset': { borderColor: mvkgTokens.surface.glassBorderStrong },
    '&:hover fieldset': { borderColor: mvkgTokens.brand.light },
    '&.Mui-focused fieldset': { borderColor: mvkgTokens.brand.main, borderWidth: 1 },
    '&.Mui-focused': { boxShadow: `0 0 0 3px ${mvkgTokens.surface.glassSoft}, 0 0 0 1px ${mvkgTokens.brand.main} inset` },
  },
  '& label.Mui-focused': { color: mvkgTokens.brand.main },
};

export default function Login() {
  const [tab, setTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
  });

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
    setInfo('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);

    try {
      if (tab === 0) {
        await login(formData.username.toLowerCase().trim(), formData.password);
        navigate('/');
      } else if (tab === 1) {
        const payload = {
          ...formData,
          username: formData.username.toLowerCase().trim(),
          email: formData.email.toLowerCase().trim(),
        };
        await register(payload);
        setTab(0);
        setError('');
        setInfo(
          'Registration submitted. Your account is awaiting administrator approval — you can sign in once approved.',
        );
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.msg || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: resetEmail.toLowerCase().trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Request failed');
      setInfo(data.message || 'Reset request submitted.');
      setResetEmail('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not submit reset request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: mvkgTokens.surface.default,
        backgroundImage: mvkgTokens.gradient.page,
        py: 6,
        px: 2,
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Logo size="lg" tagline sx={{ justifyContent: 'center', mb: 2 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, mb: 0.5 }}
          >
            Multi-View Knowledge Graph for Scientific Literature
          </Typography>
          <Kicker sx={{ display: 'block', color: 'text.disabled' }}>
            Closed-by-default · accounts require admin approval
          </Kicker>
        </Box>

        <GlassCard strong padding={4} sx={{ boxShadow: mvkgTokens.shadow.pop }}>
          <Tabs
            value={tab}
            onChange={(e, newValue) => { setTab(newValue); setError(''); setInfo(''); }}
            variant="fullWidth"
            sx={{
              mb: 1,
              '& .MuiTab-root': {
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontWeight: 700,
                fontSize: '0.78rem',
                minHeight: 44,
              },
              '& .MuiTabs-indicator': {
                height: 2,
                background: mvkgTokens.gradient.brand,
              },
            }}
          >
            <Tab label="Sign In" />
            <Tab label="Sign Up" />
            <Tab label="Reset" />
          </Tabs>

          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: `${mvkgTokens.radius.sm}px` }}>
              {error}
            </Alert>
          )}

          {info && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: `${mvkgTokens.radius.sm}px` }}>
              {info}
            </Alert>
          )}

          {tab < 2 ? (
            <form onSubmit={handleSubmit}>
              <TabPanel value={tab} index={0}>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Person color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={inputSx}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={() => setTab(2)}
                    sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: 600 }}
                  >
                    Forgot password?
                  </Link>
                </Box>
              </TabPanel>

              <TabPanel value={tab} index={1}>
                <Alert severity="warning" sx={{ mb: 1, borderRadius: `${mvkgTokens.radius.sm}px` }}>
                  Registration creates a pending request. You cannot sign in until an administrator approves your account.
                </Alert>
                <TextField
                  fullWidth
                  label="Username"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  margin="normal"
                  required
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Full Name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  margin="normal"
                  sx={inputSx}
                />
                <TextField
                  fullWidth
                  label="Password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  margin="normal"
                  required
                  helperText="Minimum 8 characters"
                  sx={inputSx}
                />
              </TabPanel>

              <AppButton
                type="submit"
                size="lg"
                disabled={loading}
                sx={{ mt: 3, width: '100%' }}
              >
                {loading ? 'Please wait…' : (tab === 0 ? 'Sign In' : 'Submit registration request')}
              </AppButton>
            </form>
          ) : (
            <form onSubmit={handlePasswordReset}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the email on your account. An administrator will process the reset (this deployment does not send automated email).
              </Typography>
              <TextField
                fullWidth
                label="Account email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                sx={inputSx}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <AppButton
                type="submit"
                size="lg"
                disabled={loading}
                sx={{ mt: 3, width: '100%' }}
              >
                {loading ? 'Submitting…' : 'Request password reset'}
              </AppButton>
              <AppButton
                variant="ghost"
                size="md"
                onClick={() => setTab(0)}
                sx={{ mt: 1, width: '100%' }}
              >
                Back to sign in
              </AppButton>
            </form>
          )}
        </GlassCard>
      </Container>
    </Box>
  );
}
